if ( typeof ProductImageZoom !== 'function' ) {

	/*
	 * The overlay is a singleton shared by every trigger on the page, but the
	 * original built its close handler bound to whichever instance happened to
	 * construct it first. That was harmless while the overlay had no state to
	 * hand back; it is not harmless once focus has to return to the trigger the
	 * user actually activated. So the opener is now tracked on the overlay node
	 * (`zoom._opener`) and the document-level handlers are installed once,
	 * rather than per instance.
	 */
	const ZOOM_STRINGS = () => ( ( window.KROWN && KROWN.settings && KROWN.settings.locales ) || {} );

	class ProductImageZoom extends HTMLElement {

		constructor(){

			super();

			if ( ! document.getElementById('zoom') ) {

				const strings = ZOOM_STRINGS();
				const closeLabel = strings.zoom_close || 'Close zoom';
				const dialogLabel = strings.zoom_dialog_label || 'Zoomed product image';

				this.zoom = document.createElement('div');
				this.zoom.id = 'zoom';
				this.zoom.setAttribute('role', 'dialog');
				this.zoom.setAttribute('aria-modal', 'true');
				this.zoom.setAttribute('aria-label', dialogLabel);
				/* Focusable so it can take initial focus. That is not just a
				   screen-reader nicety: #zoom is the scroll container, and arrow
				   keys scroll whatever is focused. The close button can't stand in
				   for it — it is position:fixed, so its containing block is the
				   viewport, and arrow keys there scroll the page behind instead of
				   panning the zoomed image. */
				this.zoom.setAttribute('tabindex', '-1');
				/* Only theme-authored SVG goes through innerHTML. The labels are
				   merchant-editable via the admin language editor, so they are set
				   with setAttribute below — a stray quote in a translation would
				   otherwise break out of the attribute. */
				this.zoom.innerHTML = `
					<img alt="" />
					<button type="button" class="zoom__exit">${KROWN.settings.symbols.zoom_out}</button>
					<span class="zoom__loader">${KROWN.settings.symbols.zoom_loader}</span>
					<div class="zoom__overlay"></div>
				`;
				document.body.append(this.zoom);

				const zoom = this.zoom;
				const exit = zoom.querySelector('.zoom__exit');
				exit.setAttribute('aria-label', closeLabel);

				exit.addEventListener('click', () => ProductImageZoom.close(zoom));

				/* Escape is the expected way out of a modal, and until now there
				   was no way out at all without a pointer. */
				document.addEventListener('keydown', (e) => {
					if ( ! zoom.classList.contains('opened') ) return;
					if ( e.key === 'Escape' ) {
						e.preventDefault();
						ProductImageZoom.close(zoom);
					} else if ( e.key === 'Tab' ) {
						/* Two-stop cycle, not a "pin it to the close button" trap:
						   the container has to stay reachable or a keyboard user can
						   never get back to arrow-scrolling the zoomed image. */
						e.preventDefault();
						if ( document.activeElement === exit ) {
							zoom.focus();
						} else {
							exit.focus();
						}
					}
				});

			} else {
				this.zoom = document.getElementById('zoom');
			}

			this.onMouseMoveHandlerBinded = this.onMouseMoveHandler.bind(this);
			this.onResizeHandlerBinded = this.onResizeHandler.bind(this);

			this.addEventListener('click', (e)=>{

				/* Remember who opened it so focus can go home on close. The
				   trigger button, not the custom element, is what was focused. */
				this.zoom._opener = this.querySelector('button') || this;
				this.zoom._panHandler = this.onMouseMoveHandlerBinded;
				this.zoom._resizeHandler = this.onResizeHandlerBinded;

				this.zoom.classList.add('opened');
				this.image = this.zoom.querySelector('img');
				this.image.src = this.getAttribute('data-image');
				/* data-alt, not the trigger's aria-label: "Zoom image N" names an
				   action, not a picture. Note media.alt falls back to the product
				   title in Liquid, so on this product all 17 resolve to the same
				   "Skin Fluff" — describing the media individually is a merchant
				   task in the admin, not something the theme can synthesise. */
				this.image.alt = this.getAttribute('data-alt') || this.zoom.getAttribute('aria-label') || '';

				/* Next frame, not now: .opened has only just been added, so the
				   dialog is still computed visibility:hidden at this point and
				   focus() on a hidden element is a no-op. */
				requestAnimationFrame(() => {
					if ( this.zoom.classList.contains('opened') ) this.zoom.focus();
				});

				if ( this.image.naturalWidth > 0 ) {
					this._productZoomMount();
				} else {
					/* once:true — src is cleared on every unmount, so naturalWidth is
					   back to 0 next time and the old code stacked a fresh load
					   listener per open, firing mount N times on the Nth zoom. */
					this.image.addEventListener('load', this._productZoomMount.bind(this), {once: true});
				}
			});

		}

		static close(zoom) {

			zoom.classList.remove('opened');

			const opener = zoom._opener;

			setTimeout(()=>{
				if ( zoom._resizeHandler ) window.removeEventListener('resize', zoom._resizeHandler);
				if ( zoom._panHandler ) window.removeEventListener('mousemove', zoom._panHandler);
				const image = zoom.querySelector('img');
				image.src = '';
				image.alt = '';
				image.className = '';
				image.style = '';
				zoom.classList.remove('loaded');
			}, 300);

			/* Restore synchronously: waiting on the 300ms transition leaves focus
			   on a button that is already visibility:hidden, and the browser
			   drops it to <body>. */
			if ( opener && typeof opener.focus === 'function' ) opener.focus();

		}

		onMouseMoveHandler(e){
			window.clientX = e.clientX;
			window.clientY = e.clientY;
			const x = e.clientX * ( window.innerWidth - this.image.offsetWidth ) / window.innerWidth;
			const y = e.clientY * ( window.innerHeight - this.image.offsetHeight ) / window.innerHeight;
			this.image.style.left = x + 'px';
			this.image.style.top = y + 'px';
		}

		onResizeHandler(){

			const rf = window.innerWidth > 768 ? 1 : 2;

			if ( this.image.classList.contains('portrait') ) {
				this.image.style.width = ( window.innerWidth*rf ) + 'px';
				this.image.style.height = ( window.innerWidth*rf / this.image.dataset.ratio ) + 'px';
			} else {

				this.image.style.height = ( window.innerHeight*rf ) + 'px';
				this.image.style.width = ( window.innerHeight*rf * this.image.dataset.ratio ) + 'px';

				if ( this.image.offsetWidth < window.innerWidth ) {
					this.image.style.width = ( window.innerWidth*rf ) + 'px';
					this.image.style.height = ( window.innerWidth*rf / this.image.dataset.ratio ) + 'px';
				}

			}

			const x = window.clientX * ( window.innerWidth - this.image.offsetWidth ) / window.innerWidth;
			const y = window.clientY * ( window.innerHeight - this.image.offsetHeight ) / window.innerHeight;

		}

		_productZoomMount(image) {

			this.image.style.left = 0;
			this.image.style.top = 0;

			window.addEventListener('mousemove', this.onMouseMoveHandlerBinded, {passive:true});

			this.image.dataset.ratio = this.image.naturalWidth / this.image.naturalHeight;
			window.addEventListener('resize', this.onResizeHandlerBinded, {passive:true});
			this.onResizeHandler();

			setTimeout(()=>{
				if ( document.body.classList.contains('touch') || document.body.classList.contains('touchevents') ) {
					this.zoom.scrollTop = ( window.innerHeight - this.image.offsetHeight ) / -2;
					this.zoom.scrollLeft = ( window.innerWidth - this.image.offsetWidth ) / -2;
				}
				this.zoom.classList.add('loaded');
			}, 50);

		}

		_productZoomUnmount() {
			ProductImageZoom.close(this.zoom);
		}

	}

  if ( typeof customElements.get('product-image-zoom') == 'undefined' ) {
		customElements.define('product-image-zoom', ProductImageZoom);
	}

}
